import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AuthRoute from '../authRoute';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { authCodeCookie, authenticateAnonymous, reloadTokenFromStorage } from '../redux/reducers/authReducer';
import { useEffect } from 'react';
import { TokenStorage } from '../helpers/tokenStorage';
import { RootState, store } from '../redux/store';
import {
  CategoryGroupRoute,
  CategoryRoute,
  DefaultWebsiteRoutes,
  GoodsItemRoute,
  GoodsRoute,
  HomeRoute,
  LoginRoute,
} from './allRoutes';
import { RouteConfig } from './routeConfig';
import { updateDocumentTitle } from '../helpers/documentHelper';
import { CompanyTitle } from '../helpers/clientConfigs';
import Cookies from 'js-cookie';
import { trackersRunner } from '../integrations/trackersRunner';
import { removeRedirectAfterLogin } from '@/helpers/localStorageService';

interface WebsiteRoutesProps {
  routes?: RouteConfig[];
}

function WebsiteRoutes(props: WebsiteRoutesProps) {
  const { routes = DefaultWebsiteRoutes } = props;
  var dispatch = useAppDispatch();
  const location = useLocation();
  const accessToken = useAppSelector((state: RootState) => state.authState.accessToken);
  const authCode = useAppSelector((state: RootState) => state.authState.anonymousLoginInfo.authCode);

  useEffect(() => {
    if ((accessToken ?? null) === (TokenStorage.Instance.getAccessToken() ?? null)) {
      return;
    }

    dispatch(reloadTokenFromStorage());
  }, [location, accessToken, dispatch]);

  useEffect(() => {
    const authCodeFromCookies = Cookies.get(authCodeCookie);
    if (!authCodeFromCookies) {
      // Some browsers doesn't remove expired cookies. Ensure it's removed
      Cookies.remove(authCodeCookie);
      return;
    }

    if (authCode === authCodeFromCookies) {
      return;
    }

    dispatch(authenticateAnonymous(authCodeFromCookies));
  }, [authCode, dispatch]);

  useEffect(() => {
    const isLoginPage = LoginRoute.getAllPaths().some((path) => matchPath(path, location.pathname));
    // Ensure redirect url is cleared if not login page
    if (!isLoginPage) {
      removeRedirectAfterLogin();
    }
  }, [location.pathname]);

  useEffect(() => {
    const pathname = location.pathname;
    const currentRoute = routes.find((route) => route.getAllPaths().some((path) => matchPath(path, pathname)));
    const routeTitle = currentRoute?.title;

    if (currentRoute === HomeRoute) {
      // reset document title on home page
      updateDocumentTitle();
    } else if (currentRoute === CategoryGroupRoute) {
      if (document.title === CompanyTitle) {
        updateDocumentTitle(getCategoryRouteTitle(currentRoute, pathname) || routeTitle);
      }
    } else if (currentRoute === CategoryRoute) {
      updateDocumentTitle(getCategoryRouteTitle(currentRoute, pathname) || routeTitle);
    } else if (currentRoute === GoodsRoute || currentRoute === GoodsItemRoute) {
      const title = getGoodsRouteTitle() || routeTitle;
      if (document.title === CompanyTitle || currentRoute === GoodsRoute) {
        updateDocumentTitle(title);
      }
    } else {
      if (document.title === CompanyTitle) {
        updateDocumentTitle(routeTitle);
      } else if (!routeTitle) {
        updateDocumentTitle();
      }
    }

    trackersRunner.trackPage();
  }, [location, routes]);

  const getCategoryRouteTitle = (categoryRoute: RouteConfig, pathname: string) => {
    const categoryId = matchPath(categoryRoute.getPath(), pathname)?.params['categoryId'];
    if (!categoryId) {
      return null;
    }

    const categoryLink = store.getState().linksState.categories.find((c) => c.id === categoryId);
    if (!categoryLink) {
      return null;
    }

    const category = store.getState().categoriesState.categories.find((c) => c.id === categoryLink.dataId);
    return category?.name;
  };

  const getGoodsRouteTitle = () => {
    var category = store.getState().categoriesState.categories.find((c) => c.itemsOnly);
    return category?.name;
  };

  return (
    <Routes>
      {routes.map((route, routeIndex) =>
        route.getAllPaths().map((path, pathIndex) => {
          let element = route.element;
          if (route.authType) {
            element = (
              <AuthRoute type={route.authType} approved={route.approvedUsers} isLoginPage={route.isLoginPage}>
                {route.element}
              </AuthRoute>
            );
          }

          return <Route key={`Key${routeIndex}-${pathIndex}`} path={path} element={element} />;
        })
      )}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}
export default WebsiteRoutes;
